const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

/**
 * Central fetch wrapper.
 *
 * Problems it solves:
 *   1. "Unexpected token '<'" — when the server (or CDN) returns an HTML page
 *      instead of JSON, res.json() throws a confusing parse error. We now check
 *      Content-Type before parsing and surface a clear message instead.
 *   2. 401 / 403 from an expired token — fires a global 'session:expired' event
 *      so App.js can log the user out and show a friendly notice.
 *   3. Network failures — wraps fetch() so a dropped connection shows a readable
 *      message rather than an uncaught TypeError.
 */
async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (networkErr) {
    throw new Error('Network error — please check your connection and try again.');
  }

  // Token expired or invalid — broadcast so App.js can log the user out
  if (res.status === 401 || res.status === 403) {
    window.dispatchEvent(new CustomEvent('session:expired'));
    throw new Error('Your session has expired. Please log in again.');
  }

  // Read the body as text first so we can attempt JSON parsing safely
  // regardless of what Content-Type the server (or a CDN/proxy) sent.
  let text;
  try {
    text = await res.text();
  } catch {
    throw new Error(`Server error (${res.status}). Please try again.`);
  }

  // Empty body (e.g. 204 No Content) — treat as success with no data
  if (!text) {
    if (!res.ok) throw new Error(`Request failed (${res.status}).`);
    return null;
  }

  // Try to parse the body as JSON
  let body;
  try {
    body = JSON.parse(text);
  } catch {
    // Body is not JSON — almost certainly an HTML error page from a CDN/proxy.
    // Log the raw response to the browser console to help with debugging.
    console.error('Non-JSON response from API:', res.status, res.url, text.slice(0, 300));
    throw new Error(
      res.ok
        ? 'Unexpected response from server. Please try again or contact support.'
        : `Server error (${res.status}). Please try again or contact support.`
    );
  }

  if (!res.ok) {
    throw new Error(body?.error || body?.message || `Request failed (${res.status}).`);
  }

  return body;
}

// ============ AUTH ============

export async function login(username, password) {
  // Login doesn't use apiFetch because a 401 here is a wrong-password, not
  // an expired session — we don't want to fire session:expired on a bad login.
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
  } catch {
    throw new Error('Network error — please check your connection and try again.');
  }

  let loginText;
  try { loginText = await res.text(); } catch { throw new Error('Network error reading response.'); }
  let loginBody;
  try {
    loginBody = JSON.parse(loginText);
  } catch {
    console.error('Non-JSON login response:', res.status, loginText.slice(0, 300));
    throw new Error('Unexpected response from server. Please try again.');
  }
  if (!res.ok) throw new Error(loginBody?.error || 'Login failed');
  return loginBody;
}

export async function changePassword(currentPassword, newPassword) {
  return apiFetch(`${API_BASE}/auth/change-password`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ currentPassword, newPassword })
  });
}

// ============ TIMESHEET (user) ============

export async function getReferenceData() {
  return apiFetch(`${API_BASE}/timesheet/reference-data`, { headers: getHeaders() });
}

export async function getTimesheetLines(month, year) {
  return apiFetch(`${API_BASE}/timesheet/lines?month=${month}&year=${year}`, { headers: getHeaders() });
}

export async function addTimesheetLine(jobId, month, year) {
  return apiFetch(`${API_BASE}/timesheet/lines`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ jobId, month, year })
  });
}

export async function deleteTimesheetLine(id) {
  return apiFetch(`${API_BASE}/timesheet/lines/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
}

export async function updateEntry(timesheetLineId, day, hours) {
  return apiFetch(`${API_BASE}/timesheet/entries`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ timesheetLineId, day, hours })
  });
}

export async function copyFromPreviousMonth(month, year) {
  return apiFetch(`${API_BASE}/timesheet/copy-previous`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ month, year })
  });
}

export async function getVacations(month, year) {
  return apiFetch(`${API_BASE}/timesheet/vacations?month=${month}&year=${year}`, { headers: getHeaders() });
}

export async function getMonthStatus(month, year) {
  return apiFetch(`${API_BASE}/timesheet/status?month=${month}&year=${year}`, { headers: getHeaders() });
}

export async function getMonthHolidays(month, year) {
  return apiFetch(`${API_BASE}/timesheet/holidays?month=${month}&year=${year}`, { headers: getHeaders() });
}

export async function closeMonth(month, year) {
  return apiFetch(`${API_BASE}/timesheet/close-month`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ month, year })
  });
}

export async function updateVacationEntry(day, month, year, hours) {
  return apiFetch(`${API_BASE}/timesheet/vacation-entry`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ day, month, year, hours })
  });
}

// ============ ADMIN ============

export async function getAdminReferenceData() {
  return apiFetch(`${API_BASE}/admin/reference-data`, { headers: getHeaders() });
}

// Cost Centers
export async function getCostCenters() {
  return apiFetch(`${API_BASE}/admin/cost-centers`, { headers: getHeaders() });
}

export async function createCostCenter(data) {
  return apiFetch(`${API_BASE}/admin/cost-centers`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateCostCenter(id, data) {
  return apiFetch(`${API_BASE}/admin/cost-centers/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteCostCenter(id) {
  return apiFetch(`${API_BASE}/admin/cost-centers/${id}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

// Customers
export async function getCustomers() {
  return apiFetch(`${API_BASE}/admin/customers`, { headers: getHeaders() });
}

export async function createCustomer(data) {
  return apiFetch(`${API_BASE}/admin/customers`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateCustomer(id, data) {
  return apiFetch(`${API_BASE}/admin/customers/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteCustomer(id) {
  return apiFetch(`${API_BASE}/admin/customers/${id}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

// Holidays
export async function getHolidays(year) {
  const url = year ? `${API_BASE}/admin/holidays?year=${year}` : `${API_BASE}/admin/holidays`;
  return apiFetch(url, { headers: getHeaders() });
}

export async function createHolidayEntry(data) {
  return apiFetch(`${API_BASE}/admin/holidays`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateHolidayEntry(id, data) {
  return apiFetch(`${API_BASE}/admin/holidays/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteHolidayEntry(id) {
  return apiFetch(`${API_BASE}/admin/holidays/${id}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

export async function syncHolidaysForYear(year) {
  return apiFetch(`${API_BASE}/admin/holidays/sync`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ year })
  });
}

// Job Types
export async function getJobTypes() {
  return apiFetch(`${API_BASE}/admin/job-types`, { headers: getHeaders() });
}

export async function createJobType(data) {
  return apiFetch(`${API_BASE}/admin/job-types`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateJobType(id, data) {
  return apiFetch(`${API_BASE}/admin/job-types/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteJobType(id) {
  return apiFetch(`${API_BASE}/admin/job-types/${id}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

// Job IDs
export async function getJobIds() {
  return apiFetch(`${API_BASE}/admin/job-ids`, { headers: getHeaders() });
}

export async function createJobId(data) {
  return apiFetch(`${API_BASE}/admin/job-ids`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateJobId(id, data) {
  return apiFetch(`${API_BASE}/admin/job-ids/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteJobId(id) {
  return apiFetch(`${API_BASE}/admin/job-ids/${id}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

// Employees
export async function getEmployees() {
  return apiFetch(`${API_BASE}/admin/employees`, { headers: getHeaders() });
}

export async function createEmployee(data) {
  return apiFetch(`${API_BASE}/admin/employees`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function updateEmployee(id, data) {
  return apiFetch(`${API_BASE}/admin/employees/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
  });
}

export async function deleteEmployee(id, confirm = false) {
  const url = confirm
    ? `${API_BASE}/admin/employees/${id}?confirm=yes`
    : `${API_BASE}/admin/employees/${id}`;
  return apiFetch(url, { method: 'DELETE', headers: getHeaders() });
}

export async function resetEmployeePassword(id, newPassword) {
  return apiFetch(`${API_BASE}/admin/employees/${id}/reset-password`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ newPassword })
  });
}

export async function getEmployeeSalaries(id, year) {
  return apiFetch(`${API_BASE}/admin/employees/${id}/salaries?year=${year}`, { headers: getHeaders() });
}

export async function updateEmployeeSalary(id, month, year, monthly_salary) {
  return apiFetch(`${API_BASE}/admin/employees/${id}/salaries`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify({ month, year, monthly_salary })
  });
}

export async function getVacationBalance(employeeId, year) {
  return apiFetch(`${API_BASE}/admin/vacation-balance/${employeeId}/${year}`, { headers: getHeaders() });
}

// Admin timesheet editing
export async function getAdminTimesheetLines(employeeId, month, year) {
  return apiFetch(`${API_BASE}/admin/timesheet/${employeeId}?month=${month}&year=${year}`, { headers: getHeaders() });
}

export async function addAdminTimesheetLine(employeeId, jobId, month, year) {
  return apiFetch(`${API_BASE}/admin/timesheet/${employeeId}/lines`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ jobId, month, year })
  });
}

export async function deleteAdminTimesheetLine(lineId) {
  return apiFetch(`${API_BASE}/admin/timesheet/lines/${lineId}`, {
    method: 'DELETE', headers: getHeaders()
  });
}

export async function updateAdminTimesheetEntry(timesheetLineId, day, hours) {
  return apiFetch(`${API_BASE}/admin/timesheet/entries`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify({ timesheetLineId, day, hours })
  });
}

// Timesheet Summary
export async function getTimesheetSummary(employeeId, year) {
  return apiFetch(`${API_BASE}/admin/summary/${employeeId}/${year}`, { headers: getHeaders() });
}

export async function toggleMonthStatus(employeeId, month, year, status) {
  return apiFetch(`${API_BASE}/admin/toggle-month-status`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ employeeId, month, year, status })
  });
}

export async function exportMonthReport(employeeId, year, month) {
  return apiFetch(`${API_BASE}/admin/export-report/${employeeId}/${year}/${month}`, { headers: getHeaders() });
}

// Backup / CSV export
export async function getBackupData(table, year) {
  const url = year
    ? `${API_BASE}/admin/backup/${table}?year=${year}`
    : `${API_BASE}/admin/backup/${table}`;
  return apiFetch(url, { headers: getHeaders() });
}
